import { css, NO, UIRectangle, UIView } from "uicore-ts"
import { CBCheckbox } from "../CB/CBCheckbox"
import { RHForms } from "./Utils_slop/RHDesignTokens"

export class RHCheckbox extends CBCheckbox {



    // Static flag to ensure CSS is only injected once
    private static hasInjectedStyles = false
    private _selectionAccent: "primary" | "success" = "primary"

    /**
     * When YES, the checkbox control is centred horizontally within the view
     * instead of sitting at the leading edge. Used for label-less checkboxes in
     * centred table columns (e.g. the procurement team role columns) so the box
     * lines up under its centred header. Default NO keeps the leading layout.
     */
    centersControlHorizontally = NO
    
    private _indeterminate = false
    
    /**
     * Indeterminate state — visually shows a dash (─) instead of a checkmark.
     * Used to indicate that one or more child nodes are selected without this
     * node itself being selected (standard tree-view convention).
     *
     * Setting this to true does NOT affect `selected`; they are independent.
     * Setting `selected` to true while indeterminate will clear indeterminate.
     */
    override get indeterminate(): boolean {
        return this._indeterminate
    }
    
    override set indeterminate(value: boolean) {
        if (this._indeterminate === value) { return }
        this._indeterminate = value
        const inputEl = this.checkbox.viewHTMLElement as HTMLInputElement
        if (inputEl) {
            inputEl.indeterminate = value
        }
    }
    
    // Override selected so that explicitly selecting clears indeterminate state
    override set selected(value: boolean) {
        super.selected = value
        if (value && this._indeterminate) {
            this.indeterminate = false
        }
    }
    
    override get selected(): boolean {
        return super.selected
    }

    get selectionAccent(): "primary" | "success" {
        return this._selectionAccent
    }

    set selectionAccent(value: "primary" | "success") {
        if (this._selectionAccent === value) { return }
        this._selectionAccent = value
        if (value === "success") {
            this.viewHTMLElement.dataset.selectionAccent = value
        }
        else {
            delete this.viewHTMLElement.dataset.selectionAccent
        }
    }
    
    
    constructor(elementID?: string) {
        
        super(elementID);
        
        // 1. Container Configuration (Bootstrap 5.3 alignment)
        (this as RHCheckbox).configureWithObject({
            style: {
                display: "flex",
                alignItems: "center",
                // padding: "4px 0"
                padding: RHForms.checkboxContainerPadding
            }
        })
        
        // 2. Label Styling
        this.titleLabel.configureWithObject({
            // textColor: RHColor.textPrimary,
            textColor: RHForms.checkboxLabelColor,
            style: {
                // fontSize: "1rem",
                fontSize: RHForms.checkboxLabelFontSize,
                // fontWeight: "400",
                fontWeight: RHForms.checkboxLabelFontWeight,
                // marginLeft: "8px",
                marginLeft: RHForms.checkboxLabelMarginLeft,
                userSelect: "none"
            }
        })
        
        // 3. Glassmorphic Checkbox Styling
        this.checkbox.configureWithObject({
            style: {
                appearance: "none",
                webkitAppearance: "none",
                width: RHForms.checkboxControlSize + "px",
                height: RHForms.checkboxControlSize + "px",
                borderRadius: RHForms.checkboxControlBorderRadius,
                backgroundColor: RHForms.checkboxControlBackgroundColor,
                backdropFilter: RHForms.checkboxControlBackdropFilter,
                //@ts-ignore
                webkitBackdropFilter: RHForms.checkboxControlBackdropFilter,
                cursor: "pointer",
                margin: "0",
                position: "relative",
                transition: RHForms.checkboxControlTransition
            }
        })
        
        this.selectionAccent = "primary"
        this.applyGlobalStyles()
        
    }
    
    
    
    
    
    private applyGlobalStyles() {
        
        if (RHCheckbox.hasInjectedStyles || this.class !== RHCheckbox) {
            return
        }
        
        const classSelector = `.${this.styleClassName}`
        const inputSelector = `${classSelector} input`
        const successInputSelector = `${classSelector}[data-selection-accent="success"] input`

        const createSelectionIconDataUrl = (pathDefinition: string, lineJoin?: string) => {
            const svgMarkup = `
                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'>
                    <path fill='none' stroke='${RHForms.checkboxCheckedIconOutlineColor}' stroke-linecap='round'${lineJoin ? ` stroke-linejoin='${lineJoin}'` : ""} stroke-width='${RHForms.checkboxCheckedIconOutlineStrokeWidth}' d='${pathDefinition}'/>
                    <path fill='none' stroke='${RHForms.checkboxCheckedIconColor}' stroke-linecap='round'${lineJoin ? ` stroke-linejoin='${lineJoin}'` : ""} stroke-width='${RHForms.checkboxCheckedIconStrokeWidth}' d='${pathDefinition}'/>
                </svg>
            `.replace(/\s+/g, " ").trim()

            return `url("data:image/svg+xml,${encodeURIComponent(svgMarkup)}")`
        }

        const checkmarkSVG = createSelectionIconDataUrl("m6 10 3 3 6-6", "round")

        // Dash mark for indeterminate: a short horizontal line centred in the box
        const indeterminateSVG = createSelectionIconDataUrl("M5 10h10")
        
        UIView.injectCSS(css`
            ${inputSelector} {
                box-shadow: ${RHForms.checkboxControlShadow};
                border: ${RHForms.checkboxControlBorderWidth} solid ${RHForms.checkboxControlBorderColor};
                box-sizing: border-box;
            }
            
            ${inputSelector}:checked {
                background-color: ${RHForms.checkboxCheckedBackgroundColor} !important;
                border-color: ${RHForms.checkboxCheckedBorderColor} !important;
                
                background-image: ${checkmarkSVG};
                background-position: center;
                background-size: ${RHForms.checkboxCheckedBackgroundSize};
                background-repeat: no-repeat;
                
                box-shadow: ${RHForms.checkboxCheckedShadow};
            }

            ${inputSelector}:indeterminate {
                background-color: ${RHForms.checkboxCheckedBackgroundColor} !important;
                border-color: ${RHForms.checkboxCheckedBorderColor} !important;

                background-image: ${indeterminateSVG};
                background-position: center;
                background-size: ${RHForms.checkboxCheckedBackgroundSize};
                background-repeat: no-repeat;

                box-shadow: ${RHForms.checkboxCheckedShadow};
            }

            ${successInputSelector}:checked {
                background-color: ${RHForms.checkboxSuccessCheckedBackgroundColor} !important;
                border-color: ${RHForms.checkboxSuccessCheckedBorderColor} !important;
                background-image: ${checkmarkSVG} !important;
                background-position: center !important;
                background-size: ${RHForms.checkboxCheckedBackgroundSize} !important;
                background-repeat: no-repeat !important;
                box-shadow: ${RHForms.checkboxSuccessCheckedShadow} !important;
            }

            ${successInputSelector}:indeterminate {
                background-color: ${RHForms.checkboxSuccessCheckedBackgroundColor} !important;
                border-color: ${RHForms.checkboxSuccessCheckedBorderColor} !important;
                background-image: ${indeterminateSVG} !important;
                background-position: center !important;
                background-size: ${RHForms.checkboxCheckedBackgroundSize} !important;
                background-repeat: no-repeat !important;
                box-shadow: ${RHForms.checkboxSuccessCheckedShadow} !important;
            }
            
            ${inputSelector}:focus {
                outline: 0;
                box-shadow: ${RHForms.checkboxFocusShadow};
                border-color: ${RHForms.checkboxFocusBorderColor};
            }

            ${successInputSelector}:focus {
                outline: 0;
                box-shadow: ${RHForms.checkboxSuccessFocusShadow} !important;
                border-color: ${RHForms.checkboxSuccessFocusBorderColor} !important;
            }
            
            ${inputSelector}:hover:not(:checked):not(:indeterminate) {
                background-color: ${RHForms.checkboxHoverBackgroundColor};
                border-color: ${RHForms.checkboxHoverBorderColor};
                box-shadow: ${RHForms.checkboxHoverShadow};
            }
            
            ${inputSelector}:active {
                /* Removed transform: scale() to avoid conflict with layout engine */
                filter: ${RHForms.checkboxActiveFilter};
            }
        `, `RHCheckbox_GlobalStyles`)
        
        RHCheckbox.hasInjectedStyles = true
    }
    
    
    override updateContentForCurrentEnabledState() {
        const wasHidden = this.hidden
        super.updateContentForCurrentEnabledState()
        this.hidden = wasHidden
        let opacity = RHForms.checkboxDisabledOpacity
        if (this.enabled) {
            opacity = "1"
        }
        this.checkbox.style.opacity = opacity
        this.titleLabel.style.opacity = opacity
        if (this.enabled) {
            this.style.cursor = "pointer"
        }
        else {
            this.style.cursor = "not-allowed"
        }
        const inputEl = this.checkbox.viewHTMLElement as HTMLInputElement
        if (inputEl) {
            inputEl.disabled = !this.enabled
        }
    }
    
    
    override layoutSubviews() {
        
        super.layoutSubviews()
        
        const bounds = this.bounds
        const checkboxSize = RHForms.checkboxControlSize

        // Clear any prefix/suffix that CBCheckbox may have previously set
        this.titleLabel.textPrefix = ""
        this.titleLabel.textSuffix = ""

        let checkboxLeadingInset = RHForms.checkboxControlLeadingInset
        if (this.centersControlHorizontally) {
            checkboxLeadingInset = Math.max(0, (bounds.width - checkboxSize) / 2)
        }

        this.checkbox.frame = new UIRectangle(
            checkboxLeadingInset,
            0,
            checkboxSize,
            checkboxSize
        )

        const labelOffset = checkboxLeadingInset + checkboxSize + RHForms.checkboxControlGap
        const labelWidth = bounds.width - labelOffset
        
        this.titleLabel.frame = new UIRectangle(labelOffset, 0, bounds.height, labelWidth)
        this.titleLabel.style.lineHeight = bounds.height + "px"
        this.titleLabel.style.height = "100%"
        
    }
    
}
